// Bán thẻ liệu trình = tạo đơn POS có dòng gói (POST /api/v2/pos/orders) → server phát hành thẻ trong cùng giao dịch.
// Thu đợt sau = thu nợ đơn POS (POST /api/v2/pos/orders/{id}/pay). Giá, chiết khấu lấy từ báo giá của backend.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, DatePicker, Input, InputNumber, Modal, Segmented, Select, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Trash2 } from 'lucide-react';
import { createPosWalkInOrder, payPosOrder, type PosOrderPayBody, type PosPaymentMethod } from '../../api/PosV2';
import { getBranches } from '../../api/Branch';
import { mixGetV1 } from '../../services/mixin';
import {
  getTreatmentPackages, quoteTreatmentPackage,
  type CardDetailDto, type PackageListItem, type PackageQuote,
} from '../../api/TreatmentV2';
import type { Customer } from '../POS/data';
import CustomerPicker from '../POS/components/CustomerPicker';
import MoneyInput from '../POS/components/MoneyInput';
import { newRequestId } from '../POS/remote';
import { KIND_LABEL } from './data';
import { addMonthsYmd, round1000, ymd } from './logic';
import { useTreatmentAuth } from './actions';
import { fmtDate, money } from './ui';

type Method = Exclude<PosPaymentMethod, 'PREPAID' | 'WALLET'>;
export const METHOD_OPTIONS: { value: Method; label: string }[] = [
  { value: 'CASH', label: 'Tiền mặt' }, { value: 'BANK_TRANSFER', label: 'Chuyển khoản' },
  { value: 'POS_CARD', label: 'Quẹt thẻ' }, { value: 'EWALLET', label: 'Ví điện tử' },
];
export const METHOD_TEXT: Record<string, string> = { CASH: 'Tiền mặt', BANK_TRANSFER: 'Chuyển khoản', POS_CARD: 'Quẹt thẻ', EWALLET: 'Ví điện tử', PREPAID: 'Ví trả trước' };
const REF_RE = /^[A-Za-z0-9]{4,50}$/;
const refOk = (m: Method, ref: string) => m === 'CASH' || REF_RE.test(ref);

/** Trả góp theo quy tắc POS: đợt 1 hôm nay ≥ 30% (dưới mức này cần quản lý duyệt), tối đa 12 đợt trong 12 tháng */
const MIN_FIRST_PCT = 30;
const MAX_INSTALLMENTS = 12;

interface PlanRow { due_date: string; amount: number }
const buildPlan = (total: number, count: number, first: number, start: string): PlanRow[] => {
  if (count <= 1) return [{ due_date: start, amount: total }];
  const rest = total - first;
  const each = round1000(rest / (count - 1));
  return Array.from({ length: count }, (_, i) => ({
    due_date: i === 0 ? start : addMonthsYmd(start, i),
    amount: i === 0 ? first : i === count - 1 ? rest - each * (count - 2) : each,
  }));
};

const Field = ({ label, required, hint, children }: { label: React.ReactNode; required?: boolean; hint?: React.ReactNode; children: React.ReactNode }) => (
  <label className="block">
    <span className="mb-1 block text-sm font-medium text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</span>
    {children}
    {hint && <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>}
  </label>
);

function MethodInput({ method, txnRef, onChange }: { method: Method; txnRef: string; onChange: (m: Method, ref: string) => void }) {
  return (
    <div className="space-y-2">
      <Segmented<Method> block value={method} onChange={m => onChange(m, txnRef)} options={METHOD_OPTIONS} />
      {method !== 'CASH' && (
        <Input value={txnRef} maxLength={50} placeholder="Mã giao dịch (VD: FT26102800001)" status={txnRef && !REF_RE.test(txnRef) ? 'error' : undefined}
          onChange={e => onChange(method, e.target.value.toUpperCase().replace(/\s/g, ''))} />
      )}
    </div>
  );
}

/** Nhân viên (tư vấn / người duyệt) — id số thật */
interface StaffOpt { id: number; name: string }
let staffCache: Promise<StaffOpt[]> | null = null;
const loadStaffs = () => {
  if (staffCache) return staffCache;
  staffCache = mixGetV1('/api/v1/be/staffs', { limit: 500 }).then((r: any) => {
    const list = r?.data?.items || r?.data?.data || (Array.isArray(r?.data) ? r.data : []);
    return list.filter((s: any) => s?.id).map((s: any) => ({ id: Number(s.id), name: s.name || s.fullname || s.full_name || `NV #${s.id}` }));
  }).catch(() => { staffCache = null; return []; });
  return staffCache;
};

const customerIdOf = (c: Customer | null) => (c && c.id.startsWith('api-') ? Number(c.id.slice(4)) || 0 : 0);

// ---------------------------------------------------------------------------
// Bán thẻ

export function SellCardApiModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone?: (cardId: number) => void }) {
  const { userName } = useTreatmentAuth();
  const [packages, setPackages] = useState<PackageListItem[]>([]);
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
  const [staffs, setStaffs] = useState<StaffOpt[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [packageId, setPackageId] = useState<number>();
  const [branchId, setBranchId] = useState<number>();
  const [consultantId, setConsultantId] = useState<number>();
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>('percent');
  const [discountValue, setDiscountValue] = useState(0);
  const [approvedBy, setApprovedBy] = useState<number>();
  const [quote, setQuote] = useState<PackageQuote | null>(null);
  const [quoteErr, setQuoteErr] = useState<{ message: string; needApproval: boolean } | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [count, setCount] = useState(1);
  const [plan, setPlan] = useState<PlanRow[]>([]);
  const [planApprovedBy, setPlanApprovedBy] = useState<number>();
  const [method, setMethod] = useState<Method>('CASH');
  const [txnRef, setTxnRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const requestId = useRef('');

  useEffect(() => {
    if (!open) return;
    requestId.current = newRequestId(); // mỗi lần mở modal một mã — gửi lại cùng mã thì server trả đơn cũ
    setCustomer(null); setPackageId(undefined); setConsultantId(undefined); setDiscountType('percent'); setDiscountValue(0);
    setApprovedBy(undefined); setQuote(null); setQuoteErr(null); setCount(1); setPlan([]); setPlanApprovedBy(undefined);
    setMethod('CASH'); setTxnRef(''); setSubmitting(false); setSubmitErr('');
    getTreatmentPackages({ is_active: true, per_page: 200 }).then(r => setPackages(r.ok && r.data ? r.data.items : []));
    getBranches({ status: 0 }).then((r: any) => {
      const list = (r?.data?.items || []).map((b: any) => ({ id: Number(b.id), name: b.name }));
      setBranches(list);
      setBranchId(b => b ?? list[0]?.id);
    });
    loadStaffs().then(setStaffs);
  }, [open]);

  const pkg = packages.find(p => p.id === packageId);

  // Báo giá lại mỗi khi đổi gói / chi nhánh / chiết khấu / người duyệt
  useEffect(() => {
    if (!open || !packageId || !branchId) { setQuote(null); return; }
    let alive = true;
    setQuoting(true);
    const t = setTimeout(() => {
      quoteTreatmentPackage(packageId, { branch_id: branchId, discount_type: discountType, discount_value: discountValue || 0, ...(approvedBy ? { approved_by: approvedBy } : {}) })
        .then(r => {
          if (!alive) return;
          setQuoting(false);
          if (r.ok && r.data) { setQuote(r.data); setQuoteErr(null); }
          else { setQuote(null); setQuoteErr({ message: r.message, needApproval: r.code === 'DISCOUNT_APPROVAL_REQUIRED' }); }
        });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [open, packageId, branchId, discountType, discountValue, approvedBy]);

  const price = quote?.final_price || 0;
  const minFirst = round1000(Math.ceil(price * MIN_FIRST_PCT / 100));
  const today = ymd();

  // Đổi giá / số đợt → dựng lại lịch mặc định (đợt 1 = 30%, các đợt sau mỗi tháng một lần)
  useEffect(() => {
    if (!price) { setPlan([]); return; }
    setPlan(buildPlan(price, count, count === 1 ? price : minFirst, today));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, count]);

  const first = plan[0]?.amount || 0;
  const planTotal = plan.reduce((s, p) => s + p.amount, 0);
  const lastDue = plan[plan.length - 1]?.due_date;
  const needPlanApproval = count > 1 && first < minFirst;
  const setRow = (i: number, patch: Partial<PlanRow>) => setPlan(plan.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  const errors: string[] = [];
  if (!customerIdOf(customer)) errors.push('Chọn khách hàng có trên hệ thống');
  if (!pkg) errors.push('Chọn gói');
  if (!branchId) errors.push('Chọn chi nhánh bán');
  if (quoteErr) errors.push(quoteErr.message);
  if (count > 1) {
    if (planTotal !== price) errors.push(`Tổng các đợt ${money(planTotal)}đ phải bằng giá thẻ ${money(price)}đ`);
    if (plan.some(r => r.amount <= 0)) errors.push('Mỗi đợt phải lớn hơn 0');
    if (lastDue && lastDue > addMonthsYmd(today, 12)) errors.push('Đợt cuối không quá 12 tháng kể từ hôm nay');
    if (needPlanApproval && !planApprovedBy) errors.push(`Đợt 1 dưới ${MIN_FIRST_PCT}% (${money(minFirst)}đ) cần chọn quản lý duyệt`);
  }
  if (first > 0 && !refOk(method, txnRef)) errors.push('Nhập mã giao dịch hợp lệ');

  const submit = async () => {
    if (errors.length || !pkg || !quote || !branchId || !customer) return;
    setSubmitting(true);
    setSubmitErr('');
    const res = await createPosWalkInOrder({
      client_request_id: requestId.current,
      customer_id: customerIdOf(customer),
      customer_name: customer.name,
      ...(customer.phone ? { customer_phone: customer.phone } : {}),
      branch_id: branchId,
      items: [{ item_type: 'package', package_id: pkg.id, quantity: 1, discount: quote.discount_amount, staff_id: consultantId ?? null }],
      payments: first > 0 ? [{ method, amount: first, ...(method !== 'CASH' ? { transaction_code: txnRef } : {}) }] : [],
      final_amount: price,
      ...(count > 1 ? { payment_type: 'INSTALLMENT', installments: plan, ...(needPlanApproval && planApprovedBy ? { plan_approved_by: planApprovedBy } : {}) } : { payment_type: 'FULL' }),
      ...(approvedBy ? { approved_by: approvedBy } : {}),
      cashier_name: userName,
    });
    setSubmitting(false);
    if (!res.ok || !res.data) {
      const hint = res.code === 'CUSTOMER_REQUIRED' ? ' – chọn khách hàng'
        : res.code === 'INSTALLMENT_PLAN_INVALID' ? ' – kiểm tra lịch thanh toán'
          : res.code === 'DISCOUNT_APPROVAL_REQUIRED' ? ' – chọn người duyệt chiết khấu' : '';
      setSubmitErr(`${res.message || 'Không bán được thẻ'}${hint}`);
      return;
    }
    const card = res.data.issued_cards?.[0];
    message.success(res.data.duplicate
      ? `Đơn ${res.data.order_code} đã tạo ở lần gửi trước – không tạo thêm thẻ`
      : `Đã phát hành thẻ ${card?.card_code || ''} (đơn ${res.data.order_code})`);
    onClose();
    if (card) onDone?.(card.card_id);
  };

  const staffOptions = staffs.map(s => ({ value: s.id, label: s.name }));

  return (
    <Modal open={open} onCancel={onClose} title="Bán thẻ liệu trình" width={920} centered destroyOnHidden cancelText="Hủy"
      okText={`Thu ${money(first)}đ & phát hành thẻ`} onOk={submit} okButtonProps={{ disabled: errors.length > 0 || quoting, loading: submitting }}>
      <div className="grid gap-5 md:grid-cols-[1fr_300px]">
        <div className="space-y-3">
          <Field label="Khách hàng" required hint="Bán gói bắt buộc có hồ sơ khách trên hệ thống">
            <CustomerPicker remote value={customer} onChange={setCustomer} debtOf={() => 0} />
          </Field>
          <Field label="Gói liệu trình" required>
            <Select className="w-full" value={packageId} onChange={v => { setPackageId(v); setDiscountValue(0); setApprovedBy(undefined); }} showSearch optionFilterProp="label"
              placeholder={packages.length ? 'Chọn gói đang bán' : 'Chưa có gói đang bán'}
              options={packages.map(p => ({ value: p.id, label: `${p.code} · ${p.name} · ${money(p.current_price.price)}đ` }))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Chi nhánh bán" required><Select className="w-full" value={branchId} onChange={setBranchId} options={branches.map(b => ({ value: b.id, label: b.name }))} /></Field>
            <Field label="Tư vấn viên"><Select className="w-full" allowClear showSearch optionFilterProp="label" value={consultantId} onChange={setConsultantId} options={staffOptions} placeholder="Chọn" /></Field>
          </div>
          <Field label="Chiết khấu" hint={quote ? `Hạn mức không cần duyệt: ${quote.discount_cap_percent}%` : 'Trên 10% cần người duyệt'}>
            <div className="flex gap-2">
              <Segmented value={discountType} onChange={v => { setDiscountType(v as 'percent' | 'amount'); setDiscountValue(0); }} options={[{ value: 'percent', label: '%' }, { value: 'amount', label: 'đ' }]} />
              {discountType === 'percent'
                ? <InputNumber className="flex-1" min={0} max={100} value={discountValue} onChange={v => setDiscountValue(Number(v) || 0)} addonAfter="%" />
                : <MoneyInput value={discountValue} onChange={setDiscountValue} max={quote?.list_price} className="!h-8 !text-sm" />}
            </div>
          </Field>
          {(quoteErr?.needApproval || approvedBy) && (
            <Field label="Người duyệt chiết khấu" required>
              <Select className="w-full" allowClear showSearch optionFilterProp="label" value={approvedBy} onChange={setApprovedBy} options={staffOptions} placeholder="Chọn quản lý duyệt" />
            </Field>
          )}
          <Field label="Thanh toán" hint={`Trả góp theo quy tắc POS: đợt 1 hôm nay, tối thiểu ${MIN_FIRST_PCT}%, tối đa ${MAX_INSTALLMENTS} đợt trong 12 tháng, không phụ thu`}>
            <Select className="w-full" value={count} onChange={setCount} disabled={!price}
              options={Array.from({ length: MAX_INSTALLMENTS }, (_, i) => ({ value: i + 1, label: i === 0 ? 'Trả 1 lần' : `Trả góp ${i + 1} đợt` }))} />
          </Field>
          {count > 1 && (
            <Field label="Lịch thanh toán" hint="Sửa ngày / số tiền từng đợt nếu khách hẹn khác lịch mặc định; đợt 1 thu ngay hôm nay">
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500">
                    <tr><th className="px-2 py-1.5 text-left">Đợt</th><th className="px-2 py-1.5 text-left">Ngày đến hạn</th><th className="px-2 py-1.5 text-right">Số tiền</th><th className="w-8" /></tr>
                  </thead>
                  <tbody>
                    {plan.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-semibold">{i + 1}</td>
                        <td className="px-2 py-1.5">
                          <DatePicker size="small" format="DD/MM/YYYY" allowClear={false} disabled={i === 0} value={dayjs(r.due_date)}
                            disabledDate={d => d.isBefore(dayjs(), 'day') || d.isAfter(dayjs().add(12, 'month'), 'day')}
                            onChange={(d: Dayjs | null) => d && setRow(i, { due_date: d.format('YYYY-MM-DD') })} />
                        </td>
                        <td className="px-2 py-1.5"><MoneyInput value={r.amount} onChange={v => setRow(i, { amount: v })} className="!h-8 !text-sm" invalid={i === 0 && r.amount < minFirst && !planApprovedBy} /></td>
                        <td className="px-1">{i > 0 && plan.length > 2 && (
                          <button type="button" onClick={() => { setPlan(plan.filter((_, k) => k !== i)); setCount(count - 1); }} className="p-1 text-slate-400 hover:text-red-500" aria-label="Xóa đợt"><Trash2 className="h-4 w-4" /></button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Field>
          )}
          {needPlanApproval && (
            <Field label={`Quản lý duyệt đợt 1 dưới ${MIN_FIRST_PCT}%`} required>
              <Select className="w-full" showSearch optionFilterProp="label" value={planApprovedBy} onChange={setPlanApprovedBy} options={staffOptions} placeholder="Chọn quản lý" />
            </Field>
          )}
          <Field label={`Phương thức thu ${money(first)}đ`}>
            <MethodInput method={method} txnRef={txnRef} onChange={(m, r) => { setMethod(m); setTxnRef(r); }} />
          </Field>
          {submitErr && <Alert type="error" showIcon message={submitErr} />}
        </div>

        <aside className="space-y-3 rounded-xl bg-slate-50 p-4 text-sm">
          {pkg ? <>
            <div>
              <div className="text-xs uppercase text-slate-500">{KIND_LABEL[pkg.kind]}</div>
              <div className="text-base font-bold">{pkg.name}</div>
              <div className="text-xs text-slate-500">Hạn dùng {pkg.validity_months} tháng – đến {fmtDate(addMonthsYmd(today, pkg.validity_months))}</div>
            </div>
            {pkg.kind === 'session' ? (
              <ul className="space-y-1">
                {pkg.items.map(i => (
                  <li key={i.service_id} className="flex justify-between gap-2">
                    <span>{i.service_name} {i.role === 'bonus' && <span className="text-xs text-green-600">(tặng)</span>}</span>
                    <b className="whitespace-nowrap">{i.sessions} buổi</b>
                  </li>
                ))}
              </ul>
            ) : <div>Mệnh giá sử dụng <b>{money(pkg.face_value)}đ</b>{pkg.items.length ? ` · ${pkg.items.length} dịch vụ áp dụng` : ' · mọi dịch vụ'}</div>}
            <div className="space-y-1 border-t border-slate-200 pt-3">
              {quoting && <div className="text-xs text-slate-500">Đang tính giá…</div>}
              {quote && <>
                <div className="flex justify-between"><span>Giá niêm yết</span><span>{money(pkg.price)}đ</span></div>
                {quote.source === 'branch' && <div className="flex justify-between"><span>Giá tại chi nhánh</span><span>{money(quote.base)}đ</span></div>}
                {quote.source === 'promo' && <div className="flex justify-between text-green-700"><span>{quote.promo_label || 'Khuyến mãi'}</span><span>{money(quote.price_today)}đ</span></div>}
                {quote.discount_amount > 0 && <div className="flex justify-between text-green-700"><span>Chiết khấu {quote.discount_percent}%</span><span>-{money(quote.discount_amount)}đ</span></div>}
                <div className="flex justify-between text-base font-bold"><span>Giá thẻ</span><span>{money(price)}đ</span></div>
                <div className="flex justify-between"><span>Thu ngay</span><b>{money(first)}đ</b></div>
                {price - first > 0 && <div className="flex justify-between text-amber-700"><span>Còn nợ theo lịch</span><b>{money(price - first)}đ</b></div>}
              </>}
            </div>
          </> : <div className="text-slate-500">Chưa chọn gói</div>}
          {errors.length > 0 && <ul className="list-disc space-y-0.5 pl-4 text-xs text-red-600">{errors.map(e => <li key={e}>{e}</li>)}</ul>}
          <p className="text-xs text-slate-500">Thẻ được lập thành đơn POS để tiền thu vào sổ quỹ ca; các đợt sau thu bằng nút "Thu đợt sau" ở chi tiết thẻ (thu nợ đơn POS).</p>
        </aside>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Thu đợt sau (thu nợ đơn POS của thẻ)

export function PayInstallmentModal({ card, onClose, onPaid }: { card: CardDetailDto | null; onClose: () => void; onPaid: () => void }) {
  const { userName } = useTreatmentAuth();
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState<Method>('CASH');
  const [txnRef, setTxnRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const next = useMemo(() => card?.installments.find(r => r.left > 0), [card]);
  const max = card?.outstanding || 0;

  useEffect(() => {
    if (!card) return;
    setAmount(Math.min(max, next?.left || max));
    setMethod('CASH'); setTxnRef(''); setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.card_id]);

  const valid = !!card?.order && amount > 0 && amount <= max && refOk(method, txnRef);
  const submit = async () => {
    if (!card?.order || !valid) return;
    setSubmitting(true);
    const body = {
      paid_amount: amount, amount, payment_method: method, is_deposit: false,
      ...(method !== 'CASH' ? { transaction_code: txnRef } : {}), cashier_name: userName,
    } as PosOrderPayBody;
    const r = await payPosOrder(card.order.id, body);
    setSubmitting(false);
    if (!r.ok) { message.error(r.message || 'Thu tiền thất bại'); return; }
    message.success(`Đã thu ${money(amount)}đ – đơn ${card.order.order_code}`);
    onPaid();
    onClose();
  };

  return (
    <Modal open={!!card} onCancel={onClose} title={card ? `Thu đợt sau · thẻ ${card.card_code}` : ''} centered destroyOnHidden width={560}
      okText={`Thu ${money(amount)}đ`} cancelText="Hủy" onOk={submit} okButtonProps={{ disabled: !valid, loading: submitting }}>
      {card && (
        <div className="space-y-3 text-sm">
          <div className="rounded-lg bg-[#ecf6f9] p-3">
            <div><b>{card.customer?.name}</b> · {card.package_name}</div>
            <div>Giá thẻ {money(card.price)}đ · Còn nợ <b className="text-red-600">{money(max)}đ</b></div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {card.installments.map(r => (
                <tr key={r.no} className="border-b border-slate-100">
                  <td className="py-1">Đợt {r.no}</td>
                  <td className="py-1">{fmtDate(r.due_date)}</td>
                  <td className="py-1 text-right">{money(r.amount)}</td>
                  <td className={`py-1 text-right text-xs ${r.state === 'overdue' ? 'text-red-600' : r.state === 'paid' ? 'text-green-600' : 'text-slate-500'}`}>
                    {r.state === 'paid' ? 'Đã thu' : r.state === 'overdue' ? `Thiếu ${money(r.left)} · trễ ${r.overdue_days} ngày` : `Thiếu ${money(r.left)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Field label="Số tiền thu" required hint="Tiền thu tự phân bổ vào đợt sớm nhất còn thiếu">
            <MoneyInput value={amount} onChange={setAmount} max={max} />
          </Field>
          <MethodInput method={method} txnRef={txnRef} onChange={(m, r) => { setMethod(m); setTxnRef(r); }} />
          <p className="text-xs text-slate-500">
            Khoản thu được ghi là thu nợ đơn POS {card.order?.order_code} để khớp sổ quỹ ca.
            {card.order?.shared && ' Đơn này có thêm hàng khác – tiền thu chia cho thẻ theo tỷ lệ giá thẻ.'}
          </p>
        </div>
      )}
    </Modal>
  );
}
